import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";

const Rents = () => {
  const [data, setData] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const daysInMonth = useMemo(() => {
    const days = new Date(year, month, 0).getDate();
    return Array.from({ length: days }, (_, i) => i + 1);
  }, [year, month]);

  const getDayOfWeek = (day) => {
    const date = new Date(year, month - 1, day);
    return date.toLocaleString("fr-FR", { weekday: "short" }).slice(0, 2);
  };

  const isWeekend = (day) => {
    const date = new Date(year, month - 1, day);
    const dow = date.getDay();
    return dow === 0 || dow === 6;
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day &&
      today.getMonth() + 1 === month &&
      today.getFullYear() === year;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}rentings/month`,
        {
          params: { month, year },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      const carsData = response.data;

      const carList = carsData.map((car) => ({
  _id: car._id,
  title: car.title,
  category: car.category,
  matricules: car.matricules.map((m) => m.value),
}));
      setAllCars(carList);

      if (!selectedCarId && carList.length > 0) {
        setSelectedCarId(carList[0]._id);
      }

      const combinedData = carsData
        .map((car) =>
          car.matricules.map((matricule) => ({
            car,
            matricule: matricule.value,
            statuses: matricule.statuses,
            category: car.category,
            unavailablePeriods: matricule.unavailablePeriods,
          }))
        )
        .flat();

      setData(combinedData);
    } catch (error) {
      console.error("Data fetch error:", error);
      setErrorMessage("Échec du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCellClick = useCallback(
    async (carId, matricule, day, currentStatus) => {
      if (loading || currentStatus === "reserved") return;
      try {
        const date = new Date(Date.UTC(year, month - 1, day));
        const isoDate = date.toISOString().split("T")[0];

        const { data: existing } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}unavailability`,
          { params: { car: carId, matricule, startDate: isoDate, endDate: isoDate } }
        );

        if (existing.length > 0) {
          await axios.delete(`${process.env.REACT_APP_BASE_URL}unavailability/${existing[0]._id}`);
        } else {
          await axios.post(`${process.env.REACT_APP_BASE_URL}unavailability`, {
            car: carId, matricule, startDate: isoDate, endDate: isoDate, source: "manual",
          });
        }
        await fetchData();
      } catch (error) {
        console.error("Update error:", error.response?.data || error.message);
        setErrorMessage("Échec de la mise à jour");
      }
    },
    [loading, fetchData, year, month]
  );

  const filteredData = useMemo(() => {
    if (!selectedCarId) return data;
    return data.filter((item) => item.car._id === selectedCarId);
  }, [data, selectedCarId]);

  const groupedData = useMemo(
    () =>
      filteredData.reduce((acc, item) => {
        acc[item.category] = acc[item.category] || [];
        acc[item.category].push(item);
        return acc;
      }, {}),
    [filteredData]
  );

  const monthName = new Date(year, month - 1).toLocaleString("fr-FR", { month: "long" });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
        .rents-page { padding: 28px; background: #f8f9fa; min-height: 100vh; font-family: 'Montserrat', sans-serif; }
        .rents-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
        .rents-title { font-size: 1.4rem; font-weight: 700; color: #1a1a1a; letter-spacing: -0.5px; }
        .rents-title span { color: #e07b00; text-transform: capitalize; }
        .controls-row { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
        .car-selector { display: flex; align-items: center; gap: 8px; background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px 12px; flex: 1; min-width: 200px; max-width: 320px; }
        .car-selector-label { font-size: 11px; font-weight: 600; color: #9ca3af; white-space: nowrap; letter-spacing: 0.5px; text-transform: uppercase; }
        .car-select { border: none; outline: none; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: #1a1a1a; background: transparent; cursor: pointer; flex: 1; }
        .month-selector { display: flex; align-items: center; gap: 8px; background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px 12px; }
        .month-select { border: none; outline: none; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: #1a1a1a; background: transparent; cursor: pointer; text-transform: capitalize; }
        .month-divider { color: #d1d5db; font-size: 16px; }
        .legend { display: flex; align-items: center; gap: 20px; margin-bottom: 24px; flex-wrap: wrap; }
        .legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; color: #6b7280; letter-spacing: 0.3px; }
        .legend-dot { width: 10px; height: 10px; border-radius: 2px; }
        .category-section { margin-bottom: 32px; }
        .category-label { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #e07b00; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
        .category-label::after { content: ''; flex: 1; height: 1px; background: #e5e7eb; }
        .table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid #e5e7eb; background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .plan-table { border-collapse: collapse; min-width: 100%; font-size: 12px; }
        .plan-table th { background: #f9fafb; border-bottom: 1px solid #e5e7eb; border-right: 1px solid #f0f0f0; padding: 0; font-weight: 600; color: #374151; }
        .th-car { padding: 12px 16px; text-align: left; min-width: 140px; position: sticky; left: 0; background: #f9fafb; z-index: 2; border-right: 2px solid #e5e7eb !important; }
        .th-day { min-width: 42px; width: 42px; text-align: center; }
        .th-day-inner { display: flex; flex-direction: column; align-items: center; padding: 6px 4px; gap: 2px; }
        .th-day-num { font-size: 13px; font-weight: 700; color: #1a1a1a; line-height: 1; }
        .th-day-name { font-size: 9px; font-weight: 500; color: #9ca3af; text-transform: uppercase; line-height: 1; }
        .th-day.weekend { background: #fef9f0; }
        .th-day.today { background: #e07b00; }
        .th-day.today .th-day-num { color: white; }
        .th-day.today .th-day-name { color: rgba(255,255,255,0.8); }
        .plan-table td { border-right: 1px solid #f0f0f0; border-bottom: 1px solid #f5f5f5; }
        .td-car { padding: 10px 16px; position: sticky; left: 0; background: white; z-index: 1; border-right: 2px solid #e5e7eb !important; }
        .car-name { font-weight: 600; color: #1a1a1a; font-size: 13px; white-space: nowrap; }
        .car-matricule { font-size: 10px; color: #9ca3af; font-weight: 500; margin-top: 2px; font-family: monospace; }
        .td-status { width: 42px; height: 44px; text-align: center; cursor: pointer; transition: all 0.15s ease; position: relative; }
        .td-status.available { background: white; }
        .td-status.available:hover { background: #f0fdf4; }
        .td-status.reserved { background: #e07b00; cursor: default; }
        .td-status.unavailable { background: #fee2e2; cursor: pointer; }
        .td-status.unavailable:hover { background: #fecaca; }
        .td-status.weekend-cell { background: #fef9f0; }
        .td-status.today-cell { box-shadow: inset 0 0 0 2px #e07b00; }
        .status-dot { width: 6px; height: 6px; border-radius: 50%; margin: 0 auto; }
        .status-dot.reserved { background: white; }
        .status-dot.unavailable { background: #ef4444; }
        .empty-state { text-align: center; padding: 48px; color: #9ca3af; font-size: 13px; }
        .error-banner { background: #fee2e2; border: 1px solid #fecaca; color: #dc2626; padding: 10px 16px; border-radius: 8px; margin-bottom: 20px; font-size: 13px; }
        .loading-overlay { position: fixed; inset: 0; background: rgba(255,255,255,0.7); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .loading-spinner { width: 28px; height: 28px; border: 3px solid #f0f0f0; border-top-color: #e07b00; border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="rents-page">
        <div className="rents-header">
          <h1 className="rents-title">
            Planning — <span>{monthName} {year}</span>
          </h1>
        </div>

        <div className="controls-row">
          <div className="car-selector">
            <span className="car-selector-label">Voiture</span>
            <select
              value={selectedCarId}
              onChange={(e) => setSelectedCarId(e.target.value)}
              className="car-select"
            >
              <option value="">Toutes les voitures</option>
{allCars.map((car) => (
  car.matricules && car.matricules.map((matricule) => (
    <option key={`${car._id}-${matricule}`} value={car._id}>
      {matricule} — {car.title}
    </option>
  ))
))}
            </select>
          </div>

          <div className="month-selector">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="month-select">
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("fr-FR", { month: "long" })}
                </option>
              ))}
            </select>
            <span className="month-divider">/</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="month-select">
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 2).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="legend">
          <div className="legend-item"><div className="legend-dot" style={{background: '#e07b00'}}></div>Réservé</div>
          <div className="legend-item"><div className="legend-dot" style={{background: '#fee2e2', border: '1px solid #fecaca'}}></div>Indisponible</div>
          <div className="legend-item"><div className="legend-dot" style={{background: 'white', border: '1px solid #e5e7eb'}}></div>Disponible</div>
          <div className="legend-item"><div className="legend-dot" style={{background: '#fef9f0', border: '1px solid #fde68a'}}></div>Weekend</div>
          <span style={{fontSize: 11, color: '#9ca3af', marginLeft: 'auto'}}>Cliquer sur une cellule pour la marquer indisponible</span>
        </div>

        {errorMessage && <div className="error-banner">{errorMessage}</div>}
        {loading && <div className="loading-overlay"><div className="loading-spinner" /></div>}
        {!loading && filteredData.length === 0 && <div className="empty-state">Aucun véhicule trouvé pour ce mois</div>}

        {Object.entries(groupedData).map(([category, items]) => (
          <div key={category} className="category-section">
            <div className="category-label">
              {category === "longueduree" ? "Longue durée" : "Courte durée"}
            </div>
            <div className="table-wrap">
              <table className="plan-table">
                <thead>
                  <tr>
                    <th className="th-car">Véhicule</th>
                    {daysInMonth.map((day) => (
                      <th key={day} className={`th-day ${isWeekend(day) ? "weekend" : ""} ${isToday(day) ? "today" : ""}`}>
                        <div className="th-day-inner">
                          <span className="th-day-num">{day}</span>
                          <span className="th-day-name">{getDayOfWeek(day)}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(({ car, matricule, statuses }) => (
                    <tr key={`${car._id}-${matricule}`}>
                      <td className="td-car">
                        <div className="car-name">{car.title}</div>
                        <div className="car-matricule">{matricule}</div>
                      </td>
                      {statuses.map((status, index) => {
                        const day = index + 1;
                        return (
                          <td
                            key={index}
                            className={`td-status ${status} ${isWeekend(day) && status === "available" ? "weekend-cell" : ""} ${isToday(day) ? "today-cell" : ""}`}
                            onClick={() => handleCellClick(car._id, matricule, day, status)}
                            title={status === "reserved" ? "Réservé" : status === "unavailable" ? "Cliquer pour rendre disponible" : "Cliquer pour bloquer"}
                          >
                            {status === "reserved" && <div className="status-dot reserved" />}
                            {status === "unavailable" && <div className="status-dot unavailable" />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default Rents;